import React, { useState } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Copy, ExternalLink, IndianRupee } from 'lucide-react';

const authHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};

export const RazorpayLinkDialog = ({ open, onClose, tours = [], prefill = {}, onCreated }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  React.useEffect(() => {
    if (open) { setForm({ ...prefill }); setCreated(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/razorpay/payment-links`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({
          amount: parseFloat(form.amount) || 0,
          description: form.description || '',
          customer_name: form.customer_name || '',
          customer_email: form.customer_email || '',
          customer_phone: form.customer_phone || '',
          tour_id: form.tour_id || '',
          customer_id: form.customer_id || '',
          due_date: form.due_date || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setCreated(data);
      toast.success('Payment link created');
      onCreated && onCreated(data);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const copyLink = () => { navigator.clipboard.writeText(created.short_url); toast.success('Link copied'); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="razorpay-link-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><IndianRupee className="w-4 h-4" />Razorpay Payment Link</DialogTitle></DialogHeader>
        {created ? (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">Share this link with the customer — status updates automatically once paid.</p>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 text-sm break-all" data-testid="razorpay-created-link">{created.short_url}</div>
            <div className="flex gap-2">
              <Button onClick={copyLink} className="flex-1" data-testid="razorpay-copy-link-btn"><Copy className="w-4 h-4 mr-1" />Copy</Button>
              <Button variant="outline" className="flex-1" onClick={() => window.open(created.short_url, '_blank')}><ExternalLink className="w-4 h-4 mr-1" />Open</Button>
            </div>
            <Button variant="ghost" className="w-full" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div><Label>Amount (₹) *</Label><Input data-testid="razorpay-amount-input" type="number" step="0.01" min="1" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></div>
            <div><Label>Description</Label><Input data-testid="razorpay-desc-input" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Advance for Meghalaya tour" /></div>
            {tours.length > 0 && (
              <div><Label>Tour</Label>
                <Select value={form.tour_id || ''} onValueChange={(v) => setForm({ ...form, tour_id: v })}>
                  <SelectTrigger data-testid="razorpay-tour-select"><SelectValue placeholder="Link to a tour (optional)" /></SelectTrigger>
                  <SelectContent>{tours.map((t) => <SelectItem key={t.id} value={t.id}>{t.tour_name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div><Label>Customer name</Label><Input value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Phone</Label><Input value={form.customer_phone || ''} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="SMS notify" /></div>
              <div><Label>Email</Label><Input value={form.customer_email || ''} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="Email notify" /></div>
            </div>
            <div><Label>Due date (for reminders)</Label><Input data-testid="razorpay-due-date" type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="razorpay-create-link-btn">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Create Link</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RazorpayLinkDialog;
